/**
 * 카메라 격자의 프레임 높이 fit. 주어진 상자에 모든 카메라가 들어가는 프레임 높이를 계산한다.
 *
 * 격자는 c6 `SyncedVideoGrid` 가 그린다. 앱이 바꿀 수 있는 값은 프레임 높이 상한(`maxHeight`)
 * 하나다. 그래서 이 파일은 c6 의 배치 규칙을 **그대로 따라** 계산한다. 앱이 열 수를 따로 정하면
 * c6 가 실제로 만든 열과 달라서 넘치거나 빈다. 따라 하는 상수와 출처:
 *
 * - 열: `repeat(auto-fit, minmax(min(18rem, 100%), 1fr))`, 간격 `0.75rem`
 *   (`c6-frontend-design/src/components/Video/SyncedVideoGrid.tsx` `Grid`).
 * - 타일: 캡션 한 줄 + `0.35rem` + 프레임(같은 파일 `Tile`). 캡션 한 줄은 caption 크기 `0.75rem`
 *   × tight 줄 높이 `1.25`(`VideoCaption.tsx`, `theme/tokens.ts`). 확대·크게 보기 버튼은 프레임
 *   안에 겹쳐 있어서 높이를 더하지 않는다.
 * - 프레임: 너비 `min(100%, maxHeight × 16/9)`, 비율 16/9 고정. 앱은 `aspectRatioMode` 를
 *   전달하지 않는다(`foundations/mediaViewportFit.ts`).
 *
 * c6 가 이 값을 바꾸면 격자가 1~2px 넘쳐 스크롤 막대가 항상 나타난다. 높이 제한에서 1px 을 빼서
 * 반올림 오차는 흡수한다. 그러나 상수 변경은 흡수하지 못한다.
 */

const GRID_GAP_REM = 0.75;
const MIN_TRACK_REM = 18;
const TILE_CHROME_REM = 0.75 * 1.25 + 0.35;
const FRAME_ASPECT = 16 / 9;
// 소수 픽셀 반올림으로 격자가 1px 넘쳐 스크롤 막대가 나타나는 것을 막는 여유.
const SAFETY_PX = 1;

/**
 * 프레임 높이를 정하는 규칙.
 *
 * - `columns: "auto"`: c6 격자의 auto-fit 열을 그대로 따른다. 상자 폭이 열 수와 프레임 폭 상한을
 *   함께 정한다.
 * - `columns: "single"`: 한 열로 쌓인다고 가정하고 **폭을 보지 않는다**. 열 폭이 이 결과를 따르는
 *   자리(분석 탭의 영상 열)에서 쓴다. 폭을 입력으로 받으면 결과가 폭을 바꾸고, 폭이 다시 결과를
 *   바꾸는 되먹임이 생긴다.
 * - `floorRem`: 이 값보다 작게 줄이지 않는다. 더 작아야 하면 격자가 자기 상자 안에서 스크롤한다.
 * - `capRem`: 이 값보다 크게 키우지 않는다. `null` 이면 상자가 허용하는 만큼 키운다.
 */
export type VideoGridFit = {
  columns: "auto" | "single";
  floorRem: number;
  capRem: number | null;
};

export type VideoGridFitInput = {
  // 격자가 놓이는 상자의 content box(px).
  width: number;
  height: number;
  // 보이는 카메라 수.
  count: number;
  // 1rem 의 px. 루트 글꼴 크기다.
  remPx: number;
  fit: VideoGridFit;
};

/**
 * 프레임 높이(px, 정수). 카메라가 없거나 상자에 아직 크기가 없으면(감춰진 탭) null 이다.
 * 그때 호출처는 직전 값을 그대로 둔다.
 */
export function fitVideoFrameHeight({
  width,
  height,
  count,
  remPx,
  fit,
}: VideoGridFitInput): number | null {
  if (count <= 0 || width <= 0 || height <= 0 || remPx <= 0) {
    return null;
  }
  const gap = GRID_GAP_REM * remPx;
  const chrome = TILE_CHROME_REM * remPx;

  let rows = count;
  let widthBound = Number.POSITIVE_INFINITY;
  if (fit.columns === "auto") {
    // auto-fit 은 카메라가 없는 트랙을 접는다. 그래서 열 수는 카메라 수를 넘지 않는다.
    const track = Math.min(MIN_TRACK_REM * remPx, width);
    const columns = Math.min(count, Math.max(1, Math.floor((width + gap) / (track + gap))));
    rows = Math.ceil(count / columns);
    widthBound = (width - (columns - 1) * gap) / columns / FRAME_ASPECT;
  }
  const heightBound = (height - (rows - 1) * gap) / rows - chrome;
  const cap = fit.capRem === null ? Number.POSITIVE_INFINITY : fit.capRem * remPx;

  // 여유는 높이 제한에만 둔다. 폭 제한에서는 프레임이 트랙을 채울 뿐 넘치지 않는다. 상한은
  // 그대로 지켜야 하는 값이다.
  const fitted = Math.floor(Math.min(heightBound - SAFETY_PX, widthBound, cap));
  return Math.max(Math.ceil(fit.floorRem * remPx), fitted);
}
